import { ChevronLeft, ChevronRight, Lock, Unlock, Loader2 } from 'lucide-react';
import { ACCENT, OK, VIOLET } from '../../lib/uiTokens';
import { MESICE_PLNE } from '../../lib/formatters';

/**
 * Chip pruh pro výběr období — jediný navigátor období v celých Financích.
 * Sedí POD záložkami v obsahové části (ne v hlavičce), aby graficky ladil se
 * zbytkem modulu a hlavička zůstala čistě jen s taby.
 *
 * Režimy (`mode`):
 *  - 'mesic' — prev/next + „Měsíc Rok"; volitelně stav měsíce + uzávěrka (admin)
 *  - 'rok'   — prev/next + „Rok"; volitelně varianty scénáře (children v pravé části)
 *
 * **Na mobilu (`isMobile`)** jsou cíle 40 px a chip období roste přes celou
 * šířku; **uzávěrka se nekreslí** — zamknout měsíc je strukturní akce a na
 * dotyku by seděla pár pixelů od navigace (norma §1b). Stav měsíce zůstává,
 * jen jako text pod názvem: ovladač odešel, stav ne.
 */

function Chip({ children, accent = null }) {
    return (
        <div className="px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{
                background: accent ? `${accent}12` : 'var(--bg-secondary)',
                color: accent || 'var(--text-secondary)',
                border: `1px solid ${accent ? accent + '55' : 'var(--border-default)'}`,
            }}>
            {children}
        </div>
    );
}

function NavBtn({ onClick, children, label, isMobile }) {
    return (
        <button onClick={onClick} aria-label={label}
            className="rounded-lg hover:opacity-70 transition-opacity flex items-center justify-center"
            style={isMobile
                ? { width: 40, height: 40, flexShrink: 0, color: 'var(--text-secondary)',
                    background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }
                : { padding: 6, color: 'var(--text-secondary)', background: 'none', border: 'none' }}>
            {children}
        </button>
    );
}

export default function ObdobiNavigator({
    mode = 'mesic',
    rok, setRok, mesic, setMesic,
    // Měsíční režim — stav měsíce + uzávěrka
    isAdmin = false, stavMesice = 'otevreny', uzavirani = false,
    onUzavrit, onZnovuOtevrit,
    // Volitelný popisek pod chipem období (např. „den 27 z 30")
    popis = null,
    isMobile = false,
    // Spodní mezera je součást pruhu u DPH a AOP; kdo si odsazení řídí sám
    // (Výsledovka má pod pruhem ještě vysvětlivku režimů), předá false.
    mezera = true,
    // Pravý slot (např. výběr AOP varianty nebo přepínač režimu Výsledovky)
    children = null,
}) {
    const posunMesic = (delta) => {
        let m = mesic + delta, r = rok;
        if (m < 1) { m = 12; r -= 1; }
        if (m > 12) { m = 1; r += 1; }
        setRok(r); setMesic(m);
    };
    const posun = (delta) => (mode === 'rok' ? setRok(rok + delta) : posunMesic(delta));

    const uzavreny = stavMesice === 'uzavreny';
    const label = mode === 'rok' ? String(rok) : `${MESICE_PLNE[mesic - 1]} ${rok}`;
    const stavBarva = uzavreny ? VIOLET : OK;

    if (isMobile) {
        return (
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                    <NavBtn onClick={() => posun(-1)} label="Předchozí" isMobile><ChevronLeft size={20} /></NavBtn>
                    <div className="flex-1 min-w-0 flex flex-col items-center justify-center rounded-lg"
                        style={{ height: 40, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}>
                        <span className="text-[13.5px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>{label}</span>
                        {/* Stav měsíce jen tam, kde se uzávěrka řídí — stejná
                            podmínka jako na desktopu. Modul, který o uzávěrce
                            nerozhoduje (Banka), by jinak hlásil „otevřený"
                            o něčem, s čím nic neudělá. */}
                        {mode === 'mesic' && (isAdmin || popis) && (
                            <span className="text-[10px]" style={{ color: isAdmin ? stavBarva : 'var(--text-secondary)' }}>
                                {isAdmin ? (uzavreny ? 'uzavřený' : 'otevřený') : ''}
                                {isAdmin && popis ? ' · ' : ''}{popis || ''}
                            </span>
                        )}
                    </div>
                    <NavBtn onClick={() => posun(1)} label="Další" isMobile><ChevronRight size={20} /></NavBtn>
                </div>
                {children}
            </div>
        );
    }

    return (
        <div className={`flex items-center gap-2.5 flex-wrap${mezera ? ' mb-5' : ''}`}>
            {/* Navigátor období */}
            <div className="flex items-center gap-1">
                <NavBtn onClick={() => posun(-1)} label="Předchozí"><ChevronLeft size={18} /></NavBtn>
                <Chip>{label}</Chip>
                <NavBtn onClick={() => posun(1)} label="Další"><ChevronRight size={18} /></NavBtn>
            </div>

            {/* Stav měsíce + uzávěrka (jen měsíční režim pro admina) */}
            {mode === 'mesic' && isAdmin && (
                <>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{
                            background: `${stavBarva}12`, color: stavBarva,
                            border: `1px solid ${stavBarva}55`,
                        }}>
                        {uzavreny ? <Lock size={12} /> : <Unlock size={12} />}
                        {uzavreny ? 'Uzavřený' : 'Otevřený'}{popis ? ` · ${popis}` : ''}
                    </span>
                    {!uzavreny && (
                        <button onClick={onUzavrit} disabled={uzavirani}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                            style={{ background: ACCENT, opacity: uzavirani ? 0.5 : 1 }}>
                            {uzavirani ? <Loader2 size={13} className="animate-spin" /> : null}
                            Uzavřít měsíc
                        </button>
                    )}
                    {uzavreny && (
                        <button onClick={onZnovuOtevrit}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                            style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}>
                            Znovu otevřít
                        </button>
                    )}
                </>
            )}

            {/* Pravý slot — např. přepínač režimu nebo výběr varianty */}
            {children && <div className="ml-auto flex items-center gap-2.5">{children}</div>}
        </div>
    );
}
